import React from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import { UsersDataService } from '../Services/UsersDataService';
import { LicenseDataService } from '../Services/LicenseDataService';
import { CompanyAccessDataService } from '../Services/CompanyAccessDataService';

export const SECURITY_STATUS = {
    RESOLVING: 'resolving',
    UNAUTHENTICATED: 'unauthenticated',
    AUTHORIZED: 'authorized',
    DENIED: 'denied',
    ERROR: 'error'
};

export const ACCESS_DENIAL_REASON = {
    NOT_REGISTERED: 'not_registered',
    INACTIVE: 'inactive',
    NO_SEAT: 'no_seat',
    LICENSE_INACTIVE: 'license_inactive',
    UNKNOWN: 'unknown'
};

const SecurityContext = React.createContext(null);

const responseText = error => {
    const problem = error?.response?.data;
    if (typeof problem === 'string') return problem.toLowerCase();
    if (!problem) return '';

    return [
        problem.code,
        problem.errorCode,
        problem.reason,
        problem.title,
        problem.detail,
        problem.message,
        problem.error
    ].filter(Boolean).join(' ').toLowerCase();
};

export const getAccessDenialReason = error => {
    const text = responseText(error);

    if (/not[_\s-]?registered|unregistered|user[_\s-]?not[_\s-]?found|usuario no registrado|usuario.*no existe/.test(text)) {
        return ACCESS_DENIAL_REASON.NOT_REGISTERED;
    }
    if (/\b(seat|slot|cupo)\b|no[_\s-]?(seat|slot)|sin.*(seat|cupo)|puesto.*licencia/.test(text)) {
        return ACCESS_DENIAL_REASON.NO_SEAT;
    }
    if (/licen[cs]e|licencia/.test(text)) {
        return ACCESS_DENIAL_REASON.LICENSE_INACTIVE;
    }
    if (/inactive|disabled|deactivated|inactivo|desactivado/.test(text)) {
        return ACCESS_DENIAL_REASON.INACTIVE;
    }
    if (error?.response?.status === 404) {
        return ACCESS_DENIAL_REASON.NOT_REGISTERED;
    }
    return ACCESS_DENIAL_REASON.UNKNOWN;
};

export const SecurityProvider = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [refreshVersion, setRefreshVersion] = React.useState(0);
    const licensedAccount = React.useRef(null);
    const [security, setSecurity] = React.useState({
        status: SECURITY_STATUS.RESOLVING,
        appUser: null,
        companyAccess: [],
        companyAccessLoading: false,
        denialReason: null,
        error: null
    });

    const account = instance.getActiveAccount() || accounts[0] || null;
    const accountId = account?.homeAccountId ?? account?.localAccountId ?? account?.username ?? '';

    const acquireAccessToken = React.useCallback(async () => {
        const currentAccount = instance.getActiveAccount() || accounts[0];
        if (!currentAccount) throw new Error('No authenticated Microsoft account is available.');
        const response = await instance.acquireTokenSilent({ ...loginRequest, account: currentAccount });
        return response.accessToken;
    }, [accounts, instance]);

    const refreshSecurity = React.useCallback(() => {
        setSecurity(current => ({ ...current, status: SECURITY_STATUS.RESOLVING, error: null }));
        setRefreshVersion(version => version + 1);
    }, []);

    const refreshCompanyAccess = React.useCallback(async () => {
        const accessToken = await acquireAccessToken();
        setSecurity(current => ({ ...current, companyAccessLoading: true }));
        try {
            const [appUser, runtime] = await Promise.all([
                UsersDataService.getCurrentUser(accessToken),
                CompanyAccessDataService.getRuntimeAccess(accessToken)
            ]);
            setSecurity(current => ({ ...current, appUser,
                status: appUser?.isActive ? SECURITY_STATUS.AUTHORIZED : SECURITY_STATUS.DENIED,
                companyAccess: appUser?.isActive ? runtime.access : [], companyAccessLoading: false }));
            return runtime.access;
        } catch (error) {
            // Fail closed: never retain stale company grants after a rejected refresh.
            setSecurity(current => ({ ...current, appUser: null, status: SECURITY_STATUS.ERROR, companyAccess: [], companyAccessLoading: false, error }));
            throw error;
        }
    }, [acquireAccessToken]);

    React.useEffect(() => {
        let cancelled = false;

        if (inProgress !== InteractionStatus.None) {
            setSecurity(current => ({ ...current, status: SECURITY_STATUS.RESOLVING }));
            return () => { cancelled = true; };
        }

        if (!isAuthenticated || !accountId) {
            licensedAccount.current = null;
            setSecurity({
                status: SECURITY_STATUS.UNAUTHENTICATED,
                appUser: null,
                companyAccess: [],
                companyAccessLoading: false,
                denialReason: null,
                error: null
            });
            return () => { cancelled = true; };
        }

        const resolveSecurity = async () => {
            setSecurity({
                status: SECURITY_STATUS.RESOLVING,
                appUser: null,
                companyAccess: [],
                companyAccessLoading: true,
                denialReason: null,
                error: null
            });

            try {
                const accessToken = await acquireAccessToken();
                const appUser = await UsersDataService.getCurrentUser(accessToken);

                if (!appUser?.isActive) {
                    if (!cancelled) {
                        setSecurity({
                            status: SECURITY_STATUS.DENIED,
                            appUser,
                            companyAccess: [],
                            companyAccessLoading: false,
                            denialReason: ACCESS_DENIAL_REASON.INACTIVE,
                            error: null
                        });
                    }
                    return;
                }

                // Explicit sign-in check: /me's functional policies no longer validate seats.
                // Keep the backend authoritative; do not infer validity from local dates or user roles.
                if (licensedAccount.current !== accountId) {
                    const license = await LicenseDataService.validateLicense(accessToken);
                    if (!license || license.status < 200 || license.status >= 300) {
                        throw Object.assign(new Error('License validation failed.'), { response: license });
                    }
                    // The existing service documents a boolean validation response.
                    if (license.data === false) {
                        throw Object.assign(new Error('License validation rejected access.'), {
                            response: { status: 403, data: { title: 'License validation rejected access.' } }
                        });
                    }
                    if (!cancelled) licensedAccount.current = accountId;
                }

                const runtime = await CompanyAccessDataService.getRuntimeAccess(accessToken);

                if (!cancelled) {
                    setSecurity({
                        status: SECURITY_STATUS.AUTHORIZED,
                        appUser,
                        companyAccess: runtime.access,
                        companyAccessLoading: false,
                        denialReason: null,
                        error: null
                    });
                }
            } catch (error) {
                if (cancelled) return;
                const status = error?.response?.status;
                const isBackendRejection = status === 401 || status === 403 || status === 404;
                setSecurity({
                    status: isBackendRejection ? SECURITY_STATUS.DENIED : SECURITY_STATUS.ERROR,
                    appUser: null,
                    companyAccess: [],
                    companyAccessLoading: false,
                    denialReason: isBackendRejection ? getAccessDenialReason(error) : null,
                    error
                });
            }
        };

        resolveSecurity();
        return () => { cancelled = true; };
    }, [accountId, acquireAccessToken, inProgress, isAuthenticated, refreshVersion]);

    const permissions = React.useMemo(
        () => security.appUser?.permissions ?? [],
        [security.appUser]
    );
    const permissionSet = React.useMemo(() => new Set(permissions), [permissions]);
    const executablePermissions = React.useMemo(
        () => security.appUser?.executablePermissions ?? [],
        [security.appUser]
    );
    const executeSet = React.useMemo(() => new Set(executablePermissions), [executablePermissions]);
    const hasPermission = React.useCallback(permission => security.status === SECURITY_STATUS.AUTHORIZED && permissionSet.has(permission), [permissionSet, security.status]);
    const canExecute = React.useCallback(
        code => hasPermission(code) && executeSet.has(code), [hasPermission, executeSet]
    );
    const hasAnyPermission = React.useCallback(
        requiredPermissions => (requiredPermissions || []).some(hasPermission),
        [hasPermission]
    );
    const companyAccess = React.useMemo(() => security.companyAccess ?? [], [security.companyAccess]);
    const hasCompanyPermission = React.useCallback((companyId, code) => (
        security.status === SECURITY_STATUS.AUTHORIZED && companyAccess.some(grant => Number(grant.companyId) === Number(companyId) && grant.resourceCode === code)
    ), [companyAccess, security.status]);
    const canExecuteForCompany = React.useCallback((companyId, code) => (
        security.status === SECURITY_STATUS.AUTHORIZED && companyAccess.some(grant => Number(grant.companyId) === Number(companyId)
            && grant.resourceCode === code && grant.canExecute)
    ), [companyAccess, security.status]);
    const companiesFor = React.useCallback(code => {
        if (security.status !== SECURITY_STATUS.AUTHORIZED) return [];
        const byId = new Map();
        companyAccess.filter(grant => grant.resourceCode === code).forEach(grant => {
            const current = byId.get(grant.companyId);
            byId.set(grant.companyId, current ? { ...current, canExecute: current.canExecute || grant.canExecute } : grant);
        });
        return [...byId.values()].sort((left, right) => left.companyName.localeCompare(right.companyName));
    }, [companyAccess, security.status]);

    const value = React.useMemo(() => ({
        ...security,
        account,
        permissions,
        executablePermissions,
        companyAccess,
        hasCompanyPermission,
        canExecuteForCompany,
        companiesFor,
        canExecute,
        isAuthorized: security.status === SECURITY_STATUS.AUTHORIZED,
        hasPermission,
        hasAnyPermission,
        acquireAccessToken,
        refreshSecurity,
        refreshCompanyAccess
    }), [account, acquireAccessToken, hasAnyPermission, hasPermission, permissions, executablePermissions, companyAccess,
        hasCompanyPermission, canExecuteForCompany, companiesFor, canExecute, refreshSecurity, refreshCompanyAccess, security]);

    return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
};

export const useSecurity = () => {
    const context = React.useContext(SecurityContext);
    if (!context) throw new Error('useSecurity must be used within SecurityProvider.');
    return context;
};
